import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

/**
 * Workshop: Floating toolbar — vertical bar with canvas control buttons.
 */

interface FloatingToolbarProps {
  zoomLevel?: number;
  isPanMode?: boolean;
  onPan?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onComment?: () => void;
  onNewFrame?: () => void;
  className?: string;
  "data-testid"?: string;
}

function ToolbarButton({
  onClick,
  label,
  testId,
  isActive,
  children,
}: {
  onClick?: () => void;
  label: string;
  testId: string;
  isActive?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex h-7 w-7 items-center justify-center rounded-lg transition-colors",
        isActive
          ? "bg-accent/15 text-accent"
          : "text-muted hover:bg-glass-stroke-subtle hover:text-primary",
      )}
      aria-label={label}
      aria-pressed={isActive}
      data-testid={testId}
    >
      {children}
      <span className="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md bg-surface px-2 py-0.5 text-canvas-base font-medium text-primary opacity-0 shadow-glass transition-opacity group-hover:opacity-100">
        {label}
      </span>
    </button>
  );
}

function ToolbarDivider() {
  return <div className="mx-auto h-px w-5 bg-glass-stroke-subtle" aria-hidden="true" />;
}

export function FloatingToolbar({
  zoomLevel = 100,
  isPanMode = false,
  onPan,
  onZoomIn,
  onZoomOut,
  onComment,
  onNewFrame,
  className,
  "data-testid": testId = "floating-toolbar",
}: FloatingToolbarProps) {
  return (
    <div
      className={cn(
        "inline-flex flex-col items-center gap-0.5 rounded-full border border-glass-stroke bg-glass-fill p-1 shadow-glass backdrop-blur-glass-sm",
        className,
      )}
      data-testid={testId}
    >
      <ToolbarButton onClick={onPan} label="Pan" testId="toolbar-pan" isActive={isPanMode}>
        <Icons.Hand className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton onClick={onZoomIn} label="Zoom in" testId="toolbar-zoom-in">
        <Icons.ZoomIn className="h-4 w-4" />
      </ToolbarButton>

      <span
        className="w-full text-center text-canvas-base font-medium text-secondary"
        data-testid="toolbar-zoom-level"
      >
        {Math.round(zoomLevel)}%
      </span>

      <ToolbarButton onClick={onZoomOut} label="Zoom out" testId="toolbar-zoom-out">
        <Icons.ZoomOut className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton onClick={onComment} label="Comment" testId="toolbar-comment">
        <Icons.MessageSquare className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton onClick={onNewFrame} label="New frame" testId="toolbar-new-frame">
        <Icons.Plus className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
}

export type { FloatingToolbarProps };
